import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';

import '../styles/PortPage.css';

import HeaderPort from '../components/HeaderPort.jsx';
import Tabs from '../components/Tabs.jsx';
import InfoCard from '../components/InfoCard.jsx';
import ServiciosList from '../components/ServiciosList.jsx';
import DescripcionContent from '../components/DescripcionContent.jsx';
import RutasList from '../components/RutasList.jsx';
import Loader from '../components/Loader.jsx';
import PuertoAlmacenamiento from '../components/PuertoAlmacenamiento';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5257';
const PLACEHOLDER_IMAGE = "https://placehold.co/1920x450/222/999?text=Puerto+Sin+Imagen";


const PUERTOS_CON_INFO_ESTATICA = [
  "Coatzacoalcos", 
  "Veracruz", 
  "Salina Cruz", 
];

export default function PuertoDetalle() {
  const [activeTab, setActiveTab] = useState('descripcion');
  const [puerto, setPuerto] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const { id } = useParams();

  useEffect(() => {
    if (!id) {
      setError("No se proporcionó un ID de puerto.");
      setLoading(false);
      return;
    }

    const fetchPuertoData = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await fetch(`${API_BASE_URL}/api/Puerto/${id}`);
        if (!response.ok) {
          if (response.status === 404) throw new Error('Puerto no encontrado.');
          throw new Error('No se pudo cargar la información del puerto.');
        }
        const data = await response.json();
        setPuerto(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchPuertoData();
  }, [id]);

  const visibleTabs = useMemo(() => {
    if (!puerto) return [];

    const tabs = [
      { id: 'descripcion', label: 'Descripción' },
      { id: 'rutas', label: 'Rutas' },
      { id: 'servicios', label: 'Servicios' }
    ];

    const tieneDatosBD = puerto.almacenamiento && puerto.almacenamiento.length > 0;
    const tieneDatosEstaticos = PUERTOS_CON_INFO_ESTATICA.some(p => 
      puerto.nombre && puerto.nombre.toLowerCase().includes(p.toLowerCase())
    );

    if (tieneDatosBD || tieneDatosEstaticos) {
      tabs.push({ id: 'almacenamiento', label: 'Almacenamiento' });
    }

    return tabs;
  }, [puerto]);

  if (loading) return <Loader />;
  if (error) return <div className="puerto-error-container">Error: {error}</div>;
  if (!puerto) return null;

  const headerSubtitle = `Puerto en ${puerto.municipio || ''}, ${puerto.estado || ''}`;
  const dbImage = puerto.url_imagen || puerto.urlImagen || puerto.UrlImagen;
  const headerImage = dbImage && dbImage.trim() !== "" ? dbImage : PLACEHOLDER_IMAGE;

  // --- VARIABLES NUEVAS ---
  // Obtenemos las variables nuevas manejando mayúsculas/minúsculas por si acaso
  const webUrl = puerto.urlWeb || puerto.UrlWeb || puerto.url_web;
  const imagenInferior = puerto.urlImagenInferior || puerto.UrlImagenInferior || puerto.url_imagen_inferior;
  // ------------------------

  return (
    <div className="puerto-page-container">
      <HeaderPort
        title={puerto.nombre}
        subtitle={headerSubtitle}
        imageUrl={headerImage}
        webUrl={webUrl} 
      />

      <Tabs 
        tabs={visibleTabs} 
        activeTab={activeTab} 
        onTabClick={setActiveTab} 
      />

      <main className="puerto-content-grid">
        <div className="puerto-main-content">
          
          {activeTab === 'descripcion' && (
            <>
                <DescripcionContent descripcion={puerto.descripcion} />
                
                {/* --- RENDERIZADO DE IMAGEN DINÁMICA --- */}
                {imagenInferior && (
                  <div className="imagen-inferior-container">
                    <img 
                      src={imagenInferior} 
                      alt={`Vista adicional de ${puerto.nombre}`}
                      className="imagen-inferior" 
                    />
                  </div>
                )}
                {/* --------------------------------------- */}
            </>
          )}

          {activeTab === 'rutas' && (
            <RutasList routes={puerto.ruta || []} />
          )}

          {activeTab === 'almacenamiento' && (
            <PuertoAlmacenamiento 
              almacenamiento={puerto.almacenamiento} 
              nombrePuerto={puerto.nombre} 
            />
          )}

          {activeTab === 'servicios' && (
            <div className="servicios-tab-content">
              <h2 style={{ color: '#e5e5e5', marginTop: 0 }}>Servicios Disponibles</h2>
              <p style={{ color: '#aaa', marginBottom: '20px' }}>
                Listado completo de las facilidades operativas y logísticas que ofrece este puerto.
              </p>
              <ServiciosList services={puerto.servicios || []} />
            </div>
          )}
        </div>

        <aside className="puerto-sidebar">
          <InfoCard 
            direccion={puerto.direccion}
            telefono={puerto.telefono}
            email={puerto.correoElectronico}
          />
          {activeTab !== 'servicios' && (
             <ServiciosList services={puerto.servicios || []} />
          )}
        </aside>
      </main>
    </div>
  );
}